// =============================================================
// Login page — Firebase Auth (Google + Email/Password)
// =============================================================
// Single screen with tab toggle: Sign In / Sign Up.
// Uses useAuth() hook for all sign-in flows.
// =============================================================

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, ArrowRight, Mail, Lock, User, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const PANEL_INITIAL = { opacity: 0, y: 20 };
const PANEL_ANIMATE = { opacity: 1, y: 0 };
const PANEL_TRANSITION = { duration: 0.5 };
const BG_STYLE = {
  backgroundImage:
    "url('https://images.unsplash.com/photo-1676220672943-5100a727ba36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxqaXUlMjBqaXRzdSUyMGRvam8lMjBtYXR8ZW58MHx8fHwxNzc5MDYzOTMyfDA&ixlib=rb-4.1.0&q=85')",
};
const FEATURE_TILES = ["JOURNAL", "ALERTS", "ROADMAP"];

function friendlyError(err) {
  const code = err?.code || "";
  if (code.includes("invalid-credential") || code.includes("wrong-password") || code.includes("user-not-found"))
    return "Invalid email or password";
  if (code.includes("email-already-in-use")) return "An account with that email already exists";
  if (code.includes("weak-password")) return "Password must be at least 6 characters";
  if (code.includes("invalid-email")) return "Invalid email address";
  if (code.includes("popup-closed-by-user")) return "Sign-in canceled";
  if (code.includes("network-request-failed")) return "Network error — check your connection";
  return err?.message || "Something went wrong";
}

export default function Login() {
  const { user, loading, signupWithEmail, loginWithEmail, loginWithGoogle, sendPasswordReset } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  async function handleGoogle() {
    setBusy(true);
    try {
      await loginWithGoogle();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password required");
      return;
    }
    setBusy(true);
    try {
      if (mode === "signup") {
        await signupWithEmail(email, password, name.trim());
        toast.success("Welcome to the mats!");
      } else {
        await loginWithEmail(email, password);
      }
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleForgot() {
    if (!email) {
      toast.error("Enter your email first, then click 'Forgot password'");
      return;
    }
    try {
      await sendPasswordReset(email);
      toast.success(`Password reset link sent to ${email}`);
    } catch (err) {
      toast.error(friendlyError(err));
    }
  }

  return (
    <div className="relative min-h-screen flex items-stretch bg-[#0A0A0A] text-white overflow-hidden">
      <div className="hidden lg:flex relative w-1/2 items-end p-12">
        <div className="absolute inset-0 bg-cover bg-center" style={BG_STYLE} />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-black/30" />
        <div className="relative z-10 max-w-md">
          <div className="inline-flex items-center gap-2 border border-zinc-700 px-3 py-1 mb-6">
            <span className="w-2 h-2 bg-red-600"></span>
            <span className="font-label text-zinc-300">Tatami Log // v1.0</span>
          </div>
          <h2 className="font-heading text-6xl leading-[0.95] mb-4">
            White belt<br />to black belt.<br />
            <span className="text-red-600">Documented.</span>
          </h2>
          <p className="text-zinc-400 text-sm leading-relaxed max-w-sm">
            A tactical training notebook for serious grapplers. Track techniques, schedule classes, and watch your roadmap fill out one roll at a time.
          </p>
        </div>
      </div>

      <div className="relative flex-1 flex items-center justify-center p-8 lg:p-16">
        <motion.div initial={PANEL_INITIAL} animate={PANEL_ANIMATE} transition={PANEL_TRANSITION} className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-red-600 flex items-center justify-center">
              <Shield className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <span className="font-heading text-2xl tracking-tight">TATAMI LOG</span>
          </div>

          <p className="font-label mb-2">Access Control</p>
          <h1 className="font-heading text-5xl mb-3">
            {mode === "signup" ? "Join the dojo." : "Step onto the mat."}
          </h1>
          <p className="text-zinc-400 text-sm leading-relaxed mb-8">
            {mode === "signup"
              ? "Create an account to log training, schedule classes, and track your roadmap."
              : "Sign in to continue your journey from white belt to black belt."}
          </p>

          <button
            onClick={handleGoogle}
            disabled={busy}
            data-testid="google-signin-button"
            className="group w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-heading text-base tracking-wider px-5 py-4 flex items-center justify-between transition-colors mb-4"
          >
            <span className="flex items-center gap-3">
              <svg className="w-5 h-5 bg-white p-0.5" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z" />
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
                <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.2-5.5l-6.6-5.4C29.6 34.6 26.9 36 24 36c-5.2 0-9.7-3.3-11.3-8l-6.6 5.1C9.5 39.7 16.2 44 24 44z" />
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.6 5.4C41.9 35.5 44 30 44 24c0-1.3-.1-2.7-.4-3.5z" />
              </svg>
              CONTINUE WITH GOOGLE
            </span>
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
          </button>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="font-mono text-[10px] text-zinc-600">OR</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === "signup" && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Display name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="signup-name-input"
                  className="w-full bg-[#141414] border border-zinc-800 pl-10 pr-3 py-3 text-sm focus:border-red-600 focus:outline-none"
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="email-input"
                className="w-full bg-[#141414] border border-zinc-800 pl-10 pr-3 py-3 text-sm focus:border-red-600 focus:outline-none"
                autoComplete="email"
                required
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="password-input"
                className="w-full bg-[#141414] border border-zinc-800 pl-10 pr-10 py-3 text-sm focus:border-red-600 focus:outline-none"
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                minLength={6}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-button"
              className="w-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-red-600 disabled:opacity-50 text-white font-heading tracking-wider py-3.5 transition-colors flex items-center justify-center gap-2"
            >
              {busy ? "..." : mode === "signup" ? "CREATE ACCOUNT" : "SIGN IN"}
            </button>
          </form>

          <div className="mt-5 flex items-center justify-between text-xs">
            <button
              onClick={() => setMode(mode === "signup" ? "login" : "signup")}
              data-testid="toggle-mode-button"
              className="text-zinc-400 hover:text-red-500 transition-colors"
            >
              {mode === "signup" ? "Already have an account? Sign in" : "New here? Create an account"}
            </button>
            {mode === "login" && (
              <button
                onClick={handleForgot}
                data-testid="forgot-password-button"
                className="text-zinc-500 hover:text-red-500 transition-colors"
              >
                Forgot password?
              </button>
            )}
          </div>

          <div className="mt-10 grid grid-cols-3 gap-3 text-center">
            {FEATURE_TILES.map((label, i) => (
              <div key={label} className="border border-zinc-800 py-4">
                <p className="font-mono text-red-600 text-xs">0{i + 1}</p>
                <p className="font-label mt-1 text-zinc-300">{label}</p>
              </div>
            ))}
          </div>

          <p className="mt-10 text-xs text-zinc-600 font-mono">
            BY CONTINUING YOU AGREE TO TRAIN HARD AND TAP OFTEN.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
